import { useEffect, useRef, useState } from 'react';
import { useReturnStore } from '@src/stores/returnStore';
import { IErrorReturnService } from '@src/models/errors';
import {
  handleAprissCall,
  handleMultiReturnCreateCall,
  handleSORISCreateCall,
  handleVersionIDCall,
  getIdentifier,
} from '@src/utils/api-helpers';
import {
  emitReturnsFlowStep,
  emitReturnsOutcome,
} from '@src/utils/returns-instrumentation-context';
import {
  AprissFinalizeRequest,
  CreateReturnOrderData,
  ICreateReturnRequest,
  IItemListCreateReturnRequest,
} from '@src/models/api';
import {
  buildAprissFinalizeRequest,
  buildSsReturnCreateRequest,
  buildFinalizeSORISRequest,
} from '@src/utils';
import { buildItemListCreateReturnRequest } from '@src/utils/itemListCreateRequest';
import { adaptReturnMethodsResponse } from '@src/utils/eligibility-utils';
import { getHighestValueMethod } from '@src/utils/general';
import { isROSEnabled } from '@src/constants/fs-values';
import { getCurrentReturnET } from '@src/utils/returnToken';
import { useNewFlowStore } from '@src/stores/newFlowStore';
import {
  RETURN_METHOD,
  RETURN_METHOD_TO_SHIPMENT_MAP,
  ReturnMethodType,
} from '@src/constants/general';
import { OrderDetailsSessionDataV2 } from '@src/models/returns';
import { useSummaryStore } from '@src/stores/summaryStore';
import { useNavigateToReturnSummary } from './useNavigateToReturnSummary';

export interface CallInformation {
  type: ReturnMethodType;
  success: boolean;
  data: CreateReturnOrderData;
}

/**
 * The identifier a leg is known by once it leaves here.
 *
 * Defined once because the gate, the alert and the report all have to agree on it.
 * A SORIS leg is named by its `sorisId`: its `returnOrderId` holds the original
 * ORDER number, which the backend fills from `getOrderNumber()`, so it is not a
 * return reference at all and must never stand in for one.
 */
const referenceOf = (call: CallInformation) =>
  call.type === RETURN_METHOD.SORIS
    ? call.data?.sorisId
    : call.data?.returnOrderId;

/**
 * No separate duplicate check, deliberately: a leg rejected as already created is
 * recorded as a success carrying `{}`, so the reference test rejects it anyway —
 * as it does a 200 that arrives without an id. Tests the same reference the report
 * will send, so the gate cannot pass a leg the report then cannot name.
 */
const isReportableCall = (call: CallInformation) =>
  call.success && Boolean(referenceOf(call));

/**
 * The created returns behind a skipped report, for the alert to name.
 *
 * Plural because these skips are scoped to the whole return: the per-leg emits in
 * callAprissFinalize carry a single `returnOrderId`, and reusing that key for a
 * joined list would break anything reading it as one id. Empty when every leg
 * came back without a reference, which is itself the thing worth seeing.
 */
const reportableIdsOf = (calls: CallInformation[]) =>
  calls.map(referenceOf).filter(Boolean).join(',');

export interface APICallState {
  currentStep: number;
  totalSteps: number;
  stopped: boolean;
  completedCalls: CallInformation[];
  callOrder: ReturnMethodType[];
  failedCallAmount: number;
  hasDuplicateError: boolean;
  initialCall: boolean;
}

const useMultiCallCreateReturn = () => {
  const {
    orderDetailsV2,
    isMilitaryExchangeGuest,
    policyValidatorResponse,
    returnShipments,
    selectedItems,
    fraudDecisions,
    rosTransactionId,
    returnMethodsResponse,
    upsGrouping,
  } = useReturnStore((state) => state);
  const methods = useNewFlowStore((state) => state.chosenReturnMethods);
  const refundValues = useSummaryStore((state) => state.refundValues);

  const [APICallState, setAPICallState] = useState<APICallState>({
    currentStep: 0,
    totalSteps: 0,
    completedCalls: [],
    callOrder: [],
    stopped: true,
    failedCallAmount: 0,
    hasDuplicateError: false,
    initialCall: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<IErrorReturnService | null>(null);
  const { currentStep, totalSteps, callOrder } = APICallState;

  // return_created is a conversion counter — the completion effect re-runs on
  // navigateToReturnSummary / selectedItems identity changes, so guard the emit
  // to fire exactly once per return (cf. hasTaggedFinalizePageRender).
  const hasEmittedReturnCreated = useRef(false);

  // Same hazard, same remedy: the ROS report also fires from an effect that
  // re-runs on those identity changes.
  const hasReportedReturn = useRef(false);

  // Hook to handle navigation to return summary
  const navigateToReturnSummary = useNavigateToReturnSummary();

  const getNewVersionId = async () => {
    const { versionId, error } = await handleVersionIDCall(
      orderDetailsV2?.orderNumber || '',
      orderDetailsV2?.emailId || '',
      isMilitaryExchangeGuest,
      orderDetailsV2?.net,
    );
    if (error) {
      setAPICallState((prev) => ({ ...prev, stopped: true }));
      setApiError(error);
      return null;
    }
    return versionId || null;
  };

  const getRequestForMethod = (
    method: ReturnMethodType,
    orderVersion: string,
  ): ICreateReturnRequest | IItemListCreateReturnRequest | null => {
    if (!orderDetailsV2) return null;
    // Only drop-off and pickup legs reach here, and both run the policy
    // validator today, so off the migration switch a missing response means
    // something has already gone wrong and there is no token to submit under.
    // On the switch the return methods response can supply one instead, which
    // is what keeps these legs submittable once the validator is narrowed and
    // stops answering for them.
    if (!isROSEnabled() && !policyValidatorResponse) return null;
    // Build and return the request object based on the method type
    const { deliveryDictionary } = orderDetailsV2;
    const shared = {
      orderDetailsV2,
      isGuest: isMilitaryExchangeGuest,
      policyValidatorET: getCurrentReturnET(),
      deliveryDictionary,
      returnShipments,
      selectedItems,
      shipmentType: RETURN_METHOD_TO_SHIPMENT_MAP[method],
      orderVersion,
    };

    // Which shape the request carries is the frontend's to choose — the marker
    // rides on the body rather than on a switch of its own, and the endpoint
    // dispatches its validation on it. What gates the choice is this caller
    // being reachable at all, which the migration switch owns.
    //
    // Keep It is not routed through here, and stays on the shipment shape by
    // design, so both shapes are live rather than only during rollout.
    if (isROSEnabled() && returnMethodsResponse) {
      return buildItemListCreateReturnRequest({
        ...shared,
        adaptedMethods: adaptReturnMethodsResponse(returnMethodsResponse),
        upsGrouping,
      });
    }
    return buildSsReturnCreateRequest(shared);
  };

  // For SORIS, we instead send SorisId and currentTime for Apriss Finalize.
  // For UPS/LORP, we send orderId of the RETURN and return creation time.
  const callAprissFinalize = (
    returnOrderId: string,
    orderDateForReturn: string,
    method: ReturnMethodType,
  ) => {
    // Both branches below used to be silent: a skipped fraud finalize left no trace
    // at all, which is why a created-but-never-finalized return can be counted but
    // not explained. `reason` is always a fixed literal — never error text or a
    // response body, since addCleanPageAction drops empty keys but does not scrub.
    if (!orderDetailsV2) {
      emitReturnsOutcome('apriss_finalize', 'skipped', {
        reason: 'missing_order_details',
        returnOrderId,
      });
      return;
    }

    const shipmentType = RETURN_METHOD_TO_SHIPMENT_MAP[method];
    let request: AprissFinalizeRequest;
    // Scoped to the builder alone: a synchronous throw out of handleAprissCall is a
    // different failure and must not be mislabelled as a build problem. Returning
    // rather than rethrowing keeps this non-fatal — Apriss finalize is already
    // fire-and-forget (handleAprissCall catches its own API failures), and
    // rethrowing escaped callAPI as an unhandled rejection, killing the remaining
    // shipment calls and stranding the customer mid-flow.
    try {
      request = buildAprissFinalizeRequest({
        orderDetailsV2,
        returnOrderId,
        orderDateForReturn,
        // Resolved again rather than carried over from the create calls. The
        // two agree because nothing reissues a token between them, not because
        // this enforces it — worth knowing if a reissue is ever added later.
        policyValidatorET: getCurrentReturnET(),
        isGuest: isMilitaryExchangeGuest,
        ...(isROSEnabled()
          ? ({ shape: 'ROS', rosTransactionId } as const)
          : ({
              shape: 'LEGACY',
              shipmentType,
              identifier: getIdentifier(fraudDecisions, shipmentType) || '0',
            } as const)),
      });
    } catch {
      emitReturnsOutcome('apriss_finalize', 'error', {
        reason: 'request_build_failed',
        returnOrderId,
      });
      return;
    }

    handleAprissCall(request);
  };

  /**
   * One unreportable leg suppresses the report for the whole return, successful
   * legs included, and it is never caught up later — deliberate, not a gap.
   *
   * PHASE_0_SORIS and APO/FPO need no branch: neither is ever pushed onto
   * callOrder, so neither can suppress the report for a mixed return.
   */
  const reportReturnOnce = (completedCalls: CallInformation[]) => {
    // Normal, not a defect: fraud is a soft dependency, so when it is unavailable
    // the methods response carries no decision and the customer completes anyway,
    // leaving nothing for the report to correlate to. Fires at the rate the fraud
    // check is down, which alerting has to be tuned for.
    //
    // Worth knowing what else goes with it. This report carries the return's
    // token, and for a return going back entirely in store it is the only call
    // that can — the in-store create request has no field for one. So on that
    // kind of return, bailing here means the token never leaves the browser at
    // all. Accepted rather than overlooked: nothing downstream reads it on that
    // path today, and the report is fire-and-forget in any case.
    if (!rosTransactionId) {
      emitReturnsOutcome('apriss_finalize', 'skipped', {
        reason: 'no_fraud_reference',
        returnOrderIds: reportableIdsOf(completedCalls),
      });
      return;
    }

    const reportableCalls = completedCalls.filter(isReportableCall);

    if (reportableCalls.length !== completedCalls.length) {
      emitReturnsOutcome('apriss_finalize', 'skipped', {
        reason: 'leg_without_reference',
        returnOrderIds: reportableIdsOf(completedCalls),
      });
      return;
    }

    // Submission order settles a tie between methods worth the same.
    const callToReport = getHighestValueMethod(selectedItems, reportableCalls);
    // Unreachable while the caller guarantees a non-empty completedCalls, but the
    // return type makes the branch mandatory, so it emits rather than exiting
    // silently — if that guarantee ever changes this surfaces instead of vanishing.
    if (!callToReport) {
      emitReturnsOutcome('apriss_finalize', 'skipped', {
        reason: 'no_leg_selected',
        returnOrderIds: reportableIdsOf(reportableCalls),
      });
      return;
    }

    callAprissFinalize(
      referenceOf(callToReport) || '',
      callToReport.data.orderDateForReturn,
      callToReport.type,
    );
  };

  const makeCreateReturnCalls = async () => {
    setAPICallState((prev) => ({
      ...prev,
      stopped: false,
      failedCallAmount: 0,
    }));
    setApiError(null);
  };

  const handleCallError = (currentCall: ReturnMethodType) => {
    setAPICallState((prev) => ({
      ...prev,
      callOrder: [
        ...prev.callOrder.filter((method) => method !== currentCall),
        currentCall,
      ],
      failedCallAmount: prev.failedCallAmount + 1,
    }));
  };

  const handleCallSuccess = (newCallInfo: CallInformation) => {
    setAPICallState((prev) => ({
      ...prev,
      currentStep: prev.currentStep + 1,
      completedCalls: [...prev.completedCalls, newCallInfo],
    }));
  };

  const handleSORISCall = async (
    currentCall: ReturnMethodType,
    orderDetails: OrderDetailsSessionDataV2,
  ) => {
    let orderVersion = orderDetails.versionId || '';

    if (!APICallState.initialCall) {
      const newVersionId = await getNewVersionId();
      if (!newVersionId) return;
      orderVersion = newVersionId;
    } else setAPICallState((prev) => ({ ...prev, initialCall: false }));

    // Pass all selectedItems so buildFinalizeSORISRequest can detect mixed shipment scenario
    // and recalculate refund for SORIS items only if needed
    const sorisRequest = await buildFinalizeSORISRequest(
      orderDetails,
      selectedItems,
      isMilitaryExchangeGuest,
      refundValues?.total || '',
      orderVersion,
    );

    const { data, error } = await handleSORISCreateCall(sorisRequest);
    if (error) {
      handleCallError(currentCall);
      setApiError(error);
      return;
    }
    if (data) {
      // SORIS carries no creation time of its own. Stamped onto the leg rather
      // than generated at the report, which under ROS happens much later.
      const sorisCreatedAt = new Date().toISOString();
      // Map SORIS response to CreateReturnOrderData format for consistency
      const newCallInfo: CallInformation = {
        type: currentCall,
        success: true,
        data: {
          orderId: data.orderId,
          returnOrderId: data.orderId,
          returnByDate: data.returnByDate,
          orderDateForReturn: sorisCreatedAt,
          returnTrackingNumbers: [],
          sorisId: data.sorisId,
          sorisQRCode: data.sorisQRCode,
        },
      };
      // Legacy only: ROS defers this to the single gated report.
      if (!isROSEnabled()) {
        callAprissFinalize(data.sorisId, sorisCreatedAt, currentCall);
      }
      handleCallSuccess(newCallInfo);
    }
  };

  const handleUPSLORPCall = async (
    currentCall: ReturnMethodType,
    orderDetails: OrderDetailsSessionDataV2,
  ) => {
    let orderVersion = orderDetails.versionId || '';

    if (!APICallState.initialCall) {
      const newVersionId = await getNewVersionId();
      if (!newVersionId) return;
      orderVersion = newVersionId;
    } else setAPICallState((prev) => ({ ...prev, initialCall: false }));

    const request = getRequestForMethod(currentCall, orderVersion);
    if (!request) return;

    const { data, error } = await handleMultiReturnCreateCall(request);
    if (error) {
      const isDuplicateError =
        error.code === 'COM_RETURN_RECORD_ALREADY_EXISTS_ERROR';
      if (isDuplicateError) {
        // If duplicate error, we can consider this call successful without data
        // Skip Apriss Finalize call since we don't have a returnOrderId
        const newCallInfo: CallInformation = {
          type: currentCall,
          success: true,
          data: {} as CreateReturnOrderData,
        };
        setAPICallState((prev) => ({
          ...prev,
          currentStep: prev.currentStep + 1,
          completedCalls: [...prev.completedCalls, newCallInfo],
          hasDuplicateError: true,
        }));
        return;
      }
      // generic error, add +1 to failedCallAmount
      handleCallError(currentCall);
      setApiError(error);
      return;
    }
    if (data) {
      const newCallInfo: CallInformation = {
        type: currentCall,
        success: true,
        data,
      };
      // Legacy only: ROS defers this to the single gated report.
      if (!isROSEnabled()) {
        callAprissFinalize(
          data.returnOrderId,
          data.orderDateForReturn,
          currentCall,
        );
      }
      handleCallSuccess(newCallInfo);
    }
  };

  const makeNextCall = async () => {
    if (currentStep >= totalSteps || isLoading) return;
    if (!orderDetailsV2) {
      setApiError({
        description: 'Missing order details',
        code: '',
        retryable: true,
      });
      setAPICallState((prev) => ({ ...prev, stopped: true }));
      return;
    }

    const currentCall = callOrder[currentStep];

    if (currentCall === RETURN_METHOD.SORIS) {
      await handleSORISCall(currentCall, orderDetailsV2);
    } else {
      await handleUPSLORPCall(currentCall, orderDetailsV2);
    }
  };

  const callAPI = async () => {
    setIsLoading(true);
    await makeNextCall();
    setIsLoading(false);
  };

  useEffect(() => {
    // if any call failed, we need to check if we reached the limit
    if (
      !APICallState.stopped &&
      APICallState.failedCallAmount > 0 &&
      currentStep >= totalSteps - APICallState.failedCallAmount
    ) {
      // stop the call process
      // New Relic: multi-shipment partial — some shipments created, others failed.
      if (APICallState.completedCalls.length > 0) {
        emitReturnsOutcome('create_return', 'partial', {
          shipmentIndex: APICallState.completedCalls.length,
          totalShipments: APICallState.totalSteps,
        });
      }
      setAPICallState((prev) => ({ ...prev, stopped: true }));
      return;
    }
    // finished, do nothing
    if (currentStep >= totalSteps && totalSteps !== 0) return;
    // only proceed if not stopped and not already loading, and still missing steps
    if (currentStep < totalSteps && !APICallState.stopped && !isLoading) {
      callAPI();
    }
  }, [APICallState, isLoading]);

  // check if all calls completed successfully
  useEffect(() => {
    const handleSuccessfulCompletion = async () => {
      if (!orderDetailsV2) return;
      // All calls finished successfully - recalculate refund and navigate to summary
      if (
        APICallState.completedCalls.length > 0 &&
        APICallState.completedCalls.length === APICallState.totalSteps
      ) {
        // New Relic: conversion — all shipment create calls succeeded. Emit once.
        if (!hasEmittedReturnCreated.current) {
          hasEmittedReturnCreated.current = true;
          emitReturnsFlowStep('return_created', { result: 'success' });
        }
        // The only point the return is known to have wholly succeeded. A leg that
        // failed and later succeeded on retry arrives here too.
        if (isROSEnabled() && !hasReportedReturn.current) {
          hasReportedReturn.current = true;
          reportReturnOnce(APICallState.completedCalls);
        }
        navigateToReturnSummary(
          APICallState.completedCalls,
          APICallState.hasDuplicateError,
        );
      }
    };

    handleSuccessfulCompletion();
  }, [
    APICallState.completedCalls,
    navigateToReturnSummary,
    orderDetailsV2,
    selectedItems,
  ]);

  // Determine the order of calls based on available methods
  useEffect(() => {
    const order: ReturnMethodType[] = [];
    if (methods.includes(RETURN_METHOD.UPS_DROP_OFF)) {
      order.push(RETURN_METHOD.UPS_DROP_OFF);
    }
    if (methods.includes(RETURN_METHOD.LORP)) {
      order.push(RETURN_METHOD.LORP);
    }
    if (methods.includes(RETURN_METHOD.SORIS)) {
      order.push(RETURN_METHOD.SORIS);
    }
    setAPICallState((prev) => ({
      ...prev,
      callOrder: order,
      totalSteps: order.length,
    }));
  }, [methods, returnShipments]);

  return {
    APICallState,
    makeCreateReturnCalls,
    apiError: APICallState.stopped ? apiError : null,
    isLoading,
  };
};

export default useMultiCallCreateReturn;
